import React, { useState } from 'react';
import { RefreshCw, Trash2, ShieldCheck, Loader2 } from 'lucide-react';
import { ConnectedWallet, WalletBalance } from '../../types';
import { NETWORKS, shortenAddress, fetchWalletBalances } from '../../utils/walletFetch';

interface ConnectedWalletsListProps {
  wallets: ConnectedWallet[];
  onRemove: (id: string) => void;
  onSync: (id: string, balances: WalletBalance[]) => void;
}

function timeAgo(ts: number | null): string {
  if (!ts) return 'Never';
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60) return 'Just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export function ConnectedWalletsList({ wallets, onRemove, onSync }: ConnectedWalletsListProps) {
  const [syncing, setSyncing] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);

  async function handleSync(wallet: ConnectedWallet) {
    setSyncing(wallet.id);
    try {
      const balances = await fetchWalletBalances(wallet.network, wallet.address);
      onSync(wallet.id, balances);
    } catch {
      // silent fail
    } finally {
      setSyncing(null);
    }
  }

  if (wallets.length === 0) return null;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="label-caps text-slate-500">Connected Wallets</p>
          <p className="text-xs text-slate-600 mt-0.5">Read-only · public addresses only</p>
        </div>
        <div className="flex items-center gap-1.5 text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-2.5 py-1">
          <ShieldCheck className="w-3.5 h-3.5" />
          <span className="text-xs font-medium">No private keys stored</span>
        </div>
      </div>

      <div className="divide-y divide-slate-700/60">
        {wallets.map((w) => {
          const cfg = NETWORKS[w.network];
          const isSyncing = syncing === w.id;
          return (
            <div
              key={w.id}
              className="flex items-center gap-3 py-3"
            >
              {/* Network dot */}
              <div
                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: cfg.color }}
              />

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-semibold text-slate-300 bg-slate-700 rounded-md px-1.5 py-0.5">
                    {cfg.symbol}
                  </span>
                  <span className="text-xs font-mono text-slate-400 truncate">
                    {shortenAddress(w.address)}
                  </span>
                </div>
                <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                  <span>${w.totalValueUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
                  <span>·</span>
                  <span>Synced {timeAgo(w.lastSynced)}</span>
                </div>
              </div>

              {/* Balances summary */}
              {w.balances.length > 0 && (
                <div className="hidden sm:flex flex-col items-end gap-1">
                  {w.balances.map((b) => (
                    <span key={b.coinId} className="text-xs text-slate-400">
                      {b.amount.toLocaleString(undefined, { maximumFractionDigits: 4 })} {b.symbol}
                    </span>
                  ))}
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center gap-1.5 ml-2">
                <button
                  onClick={() => handleSync(w)}
                  disabled={isSyncing}
                  className="p-1.5 rounded-lg text-slate-500 hover:text-slate-100 hover:bg-[rgb(var(--overlay)/0.06)] transition-colors disabled:opacity-40"
                  title="Sync balance"
                >
                  {isSyncing
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <RefreshCw className="w-4 h-4" />
                  }
                </button>

                {confirmId === w.id ? (
                  <>
                    <button
                      onClick={() => { onRemove(w.id); setConfirmId(null); }}
                      className="text-xs text-red-400 hover:text-red-300 px-2 py-1 rounded-lg hover:bg-red-500/10 transition-colors"
                    >
                      Confirm
                    </button>
                    <button
                      onClick={() => setConfirmId(null)}
                      className="text-xs text-slate-500 hover:text-slate-300 px-2 py-1 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setConfirmId(w.id)}
                    className="p-1.5 rounded-lg text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                    title="Disconnect wallet"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
